import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BadgeHelp, Ellipsis, LayoutGrid, ShoppingCart, User } from 'lucide-react'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { OfflineBanner } from './OfflineBanner'
import {
  CURRENT_SPRINT,
  SPRINT_PLAN,
  hideWhenLocked,
  isUnlocked,
  sprintOf,
  stagingActive,
} from '@waago/api'
import { MobileFrame, SprintRibbon, TabBar, type TabItem } from '@waago/ui'
import { MoreSheet } from './MoreSheet'
import { SprintGate } from './SprintGate'

/**
 * AC bottom navigation — Accueil, Demande, (•••), Aide, Compte (frame
 * 5079:20107).
 *
 * The centre button is not a fifth destination: it opens the overflow menu.
 * Notifications and Activité live behind it and in the header, which is why
 * they are not tabs here even though both are full screens.
 */
export function AppTabs() {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [more, setMore] = useState(false)

  const items: TabItem[] = (
    [
      { to: '/', label: t('nav.home'), icon: <LayoutGrid className="size-6" aria-hidden="true" /> },
      {
        to: '/activite',
        label: t('nav.request'),
        icon: <ShoppingCart className="size-6" aria-hidden="true" />,
      },
      {
        to: '/aide',
        label: t('nav.help'),
        icon: <BadgeHelp className="size-6" aria-hidden="true" />,
      },
      {
        to: '/compte',
        label: t('nav.account'),
        icon: <User className="size-6" aria-hidden="true" />,
      },
    ] satisfies TabItem[]
  )
    // A staged build marks the destinations it has not opened yet, or drops
    // them entirely — see VITE_LOCK_STYLE. The tab bar is a fixed four in the
    // frame, so dropping one is a real layout change and `badge` is the
    // default for that reason as much as any other.
    .filter((item) => !hideWhenLocked() || isUnlocked(item.to))
    .map((item) => (isUnlocked(item.to) ? item : { ...item, locked: sprintOf(item.to) }))

  return (
    <MobileFrame fill>
      {/* `min-h-0` is load-bearing: a flex child defaults to `min-height:auto`,
          so without it a tall screen grows the column instead of scrolling
          inside it, and the tab bar is pushed below the fold. */}
      {/* Only while something is held back, and above the offline banner so a
          real connectivity problem still gets the position nearest the
          content. */}
      {stagingActive() ? (
        <SprintRibbon sprint={CURRENT_SPRINT} days={SPRINT_PLAN[CURRENT_SPRINT].days} />
      ) : null}
      <OfflineBanner />
      <main id="main" className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
        <SprintGate>
          <Outlet />
        </SprintGate>
      </main>

      <TabBar
        items={items}
        activePath={location.pathname}
        onNavigate={(to) => void navigate(to)}
        action={{
          label: 'Plus d’options',
          icon: <Ellipsis className="size-6" aria-hidden="true" />,
          onPress: () => setMore(true),
        }}
      />

      <MoreSheet open={more} onClose={() => setMore(false)} />
    </MobileFrame>
  )
}
